import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OfferManagement.css';

function OfferManagement({ listingId, userType, onOfferAccepted }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counterModal, setCounterModal] = useState(null);
  const [counterAmount, setCounterAmount] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchOffers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/offers/listing/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle both array response and { offers: [...] } response
      const offersData = Array.isArray(response.data) ? response.data : (response.data.offers || []);
      setOffers(offersData);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (offerId) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/offers/${offerId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (onOfferAccepted) onOfferAccepted(response.data.transactionId);
      fetchOffers();
    } catch (error) {
      alert(error.response?.data?.error || 'Error accepting offer');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (offerId) => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/offers/${offerId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchOffers();
    } catch (error) {
      alert(error.response?.data?.error || 'Error rejecting offer');
    } finally {
      setProcessing(false);
    }
  };

  const handleCounter = async () => {
    if (!counterAmount || counterAmount <= 0) {
      alert('Please enter a valid counter amount');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/offers/${counterModal.id}/counter`, {
        counter_amount: parseFloat(counterAmount),
        counter_terms: counterMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCounterModal(null);
      setCounterAmount('');
      setCounterMessage('');
      fetchOffers();
    } catch (error) {
      alert(error.response?.data?.error || 'Error submitting counter offer');
    } finally {
      setProcessing(false);
    }
  };

  // Helper to get buyer display name (handles both external and platform offers)
  const getBuyerName = (offer) => {
    if (offer.buyer_name) {
      return offer.buyer_name;
    }
    if (offer.buyer_first_name || offer.buyer_last_name) {
      return `${offer.buyer_first_name || ''} ${offer.buyer_last_name || ''}`.trim();
    }
    return 'Unknown Buyer';
  };

  // Helper to get buyer email
  const getBuyerEmail = (offer) => {
    return offer.buyer_email || offer.registered_buyer_email || 'Not provided';
  };

  if (loading) return <div className="loading">Loading offers...</div>;

  return (
    <div className="offer-management">
      <div className="offer-header">
        <h3>Offers Received</h3>
        <span className="offer-count">{offers.length} offer{offers.length !== 1 ? 's' : ''}</span>
      </div>

      {offers.length === 0 ? (
        <div className="no-offers">
          <p>No offers received yet.</p>
        </div>
      ) : (
        <div className="offers-grid">
          {offers.map(offer => {
            return (
              <div key={offer.id} className="offer-card">
                {/* Offer Amount - Prominent Display */}
                <div className="offer-amount-display">
                  <span className="amount-label">Offer Amount</span>
                  <span className="amount-value">${offer.offer_amount?.toLocaleString()}</span>
                </div>

                {/* Buyer Info */}
                <div className="offer-info-section">
                  <div className="info-row">
                    <span className="info-label">Buyer</span>
                    <span className="info-value">{getBuyerName(offer)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Email</span>
                    <span className="info-value">{getBuyerEmail(offer)}</span>
                  </div>
                  {offer.buyer_phone && (
                    <div className="info-row">
                      <span className="info-label">Phone</span>
                      <span className="info-value">{offer.buyer_phone}</span>
                    </div>
                  )}
                  {offer.buyer_agent && (
                    <div className="info-row">
                      <span className="info-label">Buyer's Agent</span>
                      <span className="info-value">{offer.buyer_agent}</span>
                    </div>
                  )}
                  {offer.financing_type && (
                    <div className="info-row">
                      <span className="info-label">Financing</span>
                      <span className="info-value" style={{ textTransform: 'capitalize' }}>
                        {offer.financing_type.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                  {offer.earnest_money && (
                    <div className="info-row">
                      <span className="info-label">Earnest Money</span>
                      <span className="info-value">${Number(offer.earnest_money).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="info-label">Closing Date</span>
                    <span className="info-value">
                      {offer.closing_date ? new Date(offer.closing_date).toLocaleDateString() : 'Not specified'}
                    </span>
                  </div>
                </div>

                {/* Message if exists */}
                {offer.message && (
                  <div className="offer-message-section">
                    <span className="message-label">Buyer's Message</span>
                    <p className="message-text">{offer.message}</p>
                  </div>
                )}

                {/* Contingencies if exists */}
                {offer.contingencies && (
                  <div className="offer-contingencies-section">
                    <span className="contingencies-label">Contingencies</span>
                    <p className="contingencies-text">{offer.contingencies}</p>
                  </div>
                )}

                {/* Notes if exists (for external offers) */}
                {offer.notes && (
                  <div className="offer-message-section">
                    <span className="message-label">Notes</span>
                    <p className="message-text">{offer.notes}</p>
                  </div>
                )}

                {/* Action Buttons - only show for pending offers */}
                {offer.status === 'pending' && (
                  <div className="offer-card-actions">
                    <button 
                      className="btn btn-action" 
                      onClick={() => handleAccept(offer.id)}
                      disabled={processing}
                    >
                      Accept
                    </button>
                    <button 
                      className="btn btn-action"
                      onClick={() => setCounterModal(offer)}
                      disabled={processing}
                    >
                      Counter
                    </button>
                    <button 
                      className="btn btn-action"
                      onClick={() => handleReject(offer.id)}
                      disabled={processing}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Counter Offer Modal */}
      {counterModal && (
        <div className="modal-overlay" onClick={() => setCounterModal(null)}>
          <div className="counter-modal" onClick={(e) => e.stopPropagation()}>
            <div className="counter-modal-header">
              <div className="counter-modal-icon">$</div>
              <h3>Counter Offer</h3>
              <button className="counter-modal-close" onClick={() => setCounterModal(null)}>×</button>
            </div>
            
            <div className="counter-modal-body">
              {/* Original Offer Summary */}
              <div className="original-offer-summary">
                <h4>Original Offer</h4>
                <div className="original-offer-grid">
                  <div className="original-offer-item">
                    <span className="item-label">BUYER</span>
                    <span className="item-value">{getBuyerName(counterModal)}</span>
                  </div>
                  <div className="original-offer-item">
                    <span className="item-label">OFFER AMOUNT</span>
                    <span className="item-value">${counterModal.offer_amount?.toLocaleString()}</span>
                  </div>
                  <div className="original-offer-item">
                    <span className="item-label">CLOSING DATE</span>
                    <span className="item-value">
                      {counterModal.closing_date 
                        ? new Date(counterModal.closing_date).toLocaleDateString() 
                        : 'Not specified'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Counter Terms */}
              <div className="counter-terms-section">
                <h4>Your Counter Terms</h4>
                
                <div className="form-group">
                  <label>Counter Amount <span className="required">*</span></label>
                  <div className="input-with-prefix">
                    <span>$</span>
                    <input 
                      type="text"
                      value={counterAmount ? Number(counterAmount).toLocaleString() : ''}
                      onChange={(e) => setCounterAmount(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="Enter your counter amount"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Additional Terms (Optional)</label>
                  <textarea
                    value={counterMessage}
                    onChange={(e) => setCounterMessage(e.target.value)}
                    placeholder="Any other conditions, contingencies, or notes..."
                    rows={3}
                  />
                </div>

                <div className="counter-note">
                  <p>💡 For external offers, you'll need to communicate this counter offer to the buyer's agent directly.</p>
                </div>
              </div>
            </div>

            <div className="counter-modal-footer">
              <button className="btn btn-cancel" onClick={() => setCounterModal(null)}>
                Cancel
              </button>
              <button 
                className="btn btn-submit" 
                onClick={handleCounter}
                disabled={processing || !counterAmount}
              >
                {processing ? 'Sending...' : 'Save Counter Offer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Buyer's view of their offers with counter-offer handling
export function BuyerOfferView({ offerId, onCounterAccepted }) {
  const [offer, setOffer] = useState(null);
  const [counterOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchOfferDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/offers/my-offers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const found = response.data.offers?.find(o => o.id === offerId);
      setOffer(found);
    } catch (error) {
      console.error('Error fetching offer:', error);
    } finally {
      setLoading(false);
    }
  };

  const acceptCounterOffer = async (counterOfferId) => {
    if (!window.confirm('Are you sure you want to accept this counter offer?')) return;
    
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/offers/counter/${counterOfferId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Counter offer accepted! Transaction created.');
      if (onCounterAccepted) onCounterAccepted(response.data.transactionId);
    } catch (error) {
      alert(error.response?.data?.error || 'Error accepting counter offer');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!offer) return <div>Offer not found</div>;

  return (
    <div className="buyer-offer-view">
      {offer.status === 'countered' && counterOffer && (
        <div className="counter-offer-alert">
          <h4>You Have a Counter Offer!</h4>
          <p>The seller has countered with <strong>${counterOffer.counter_amount?.toLocaleString()}</strong></p>
          {counterOffer.message && <p className="counter-message">"{counterOffer.message}"</p>}
          <div className="counter-actions">
            <button 
              className="btn btn-action"
              onClick={() => acceptCounterOffer(counterOffer.id)}
              disabled={processing}
            >
              Accept Counter Offer
            </button>
            <button className="btn btn-secondary">
              Decline
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OfferManagement;
